/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View, Text } from '@components/atoms'
import { IconButton, Tooltip, Chip, Button } from '@mui/material'
import { Close, DragIndicator, WarningAmber } from '@mui/icons-material'
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd'
import {
	mono,
	boShared,
	boMotion,
} from '@components/systems/backoffice/backoffice.tokens'

import { SeasonType } from '@schemas'
import { useAppDispatch } from '@redux/index'
import { seasonActions } from '@redux/store/index'
import dayjs from 'dayjs'

interface PropsType {
	/** 대기열에 있는 시즌 (queueOrder 오름차순) */
	queueList: SeasonType[]
	selectedSeasonId: string
	onSeasonClick: (season: SeasonType) => void
	/** 이 시즌을 골라 세부 정보로 데려간다. 판매 시작은 거기서 한다. */
	onOpenClick: (season: SeasonType) => void
}

/**
 * 판매 대기열.
 *
 * 순서는 "다음에 열 순서"를 뜻할 뿐 자동 전환을 예약하지 않는다.
 * 실제 전환은 사람이 누르고, 판매 기간 안에서만 가능하다.
 * (SEASON_OPEN_PLAN.md §7)
 */
export const SeasonQueueList = (props: PropsType) => {
	const { queueList, selectedSeasonId, onSeasonClick, onOpenClick } = props
	const reduxDispatch = useAppDispatch()

	/**
	 * 앞 순번인데 시작일이 더 늦은 시즌이 있으면 날짜 역전이다.
	 * 드래그 중에는 모달 대신 인라인 배지로만 알린다.
	 */
	const invertedIdSet: Set<string> = React.useMemo(() => {
		const res = new Set<string>()

		queueList.forEach((aSeason, idx) => {
			const hasLaterAhead = queueList
				.slice(0, idx)
				.some(anEarlier => dayjs(anEarlier.startDate).isAfter(dayjs(aSeason.startDate)))

			if (hasLaterAhead) {
				res.add(aSeason.id)
			}
		})

		return res
	}, [queueList])

	const onDragEnd = (result: DropResult) => {
		if (!result.destination || result.destination.index === result.source.index) {
			return
		}

		const newList = [...queueList]
		const [moved] = newList.splice(result.source.index, 1)
		newList.splice(result.destination.index, 0, moved)

		reduxDispatch(seasonActions.UPDATE_SEASON_QUEUE(newList.map(aSeason => aSeason.id)))
	}

	const onRemoveClick = (seasonId: string) => {
		reduxDispatch(
			seasonActions.UPDATE_SEASON_QUEUE(
				queueList.filter(aSeason => aSeason.id !== seasonId).map(aSeason => aSeason.id),
			),
		)
	}

	if (queueList.length === 0) {
		return (
			<View css={style}>
				<View className="emptyView">
					<Text className="emptyText">판매 대기열이 비어 있습니다.</Text>
					<Text className="emptyHint">
						시즌 목록에서 &ldquo;대기열에 추가&rdquo;를 눌러 다음에 열 시즌을
						준비해두세요.
					</Text>
				</View>
			</View>
		)
	}

	return (
		<View css={style}>
			<DragDropContext onDragEnd={onDragEnd}>
				<Droppable droppableId="seasonQueue">
					{droppableProvided => (
						<div ref={droppableProvided.innerRef} {...droppableProvided.droppableProps}>
							{queueList.map((aSeason, idx) => {
								const isInverted = invertedIdSet.has(aSeason.id)
								const isInSalePeriod =
									!dayjs().startOf('day').isBefore(dayjs(aSeason.startDate)) &&
									!dayjs().startOf('day').isAfter(dayjs(aSeason.endDate))

								return (
									<Draggable key={aSeason.id} draggableId={aSeason.id} index={idx}>
										{(draggableProvided, snapshot) => (
											<div
												ref={draggableProvided.innerRef}
												{...draggableProvided.draggableProps}
												className={`queueItem ${snapshot.isDragging ? 'dragging' : ''} ${
													selectedSeasonId === aSeason.id ? 'selected' : ''
												}`}
												onClick={() => onSeasonClick(aSeason)}
											>
												<div
													className="dragHandle"
													{...draggableProvided.dragHandleProps}
												>
													<DragIndicator fontSize="small" />
												</div>

												<span className="orderBadge">
													{String(idx + 1).padStart(2, '0')}
												</span>

												<div className="queueContent">
													<div className="queueHeader">
														<Text className="queueTitle">{aSeason.title}</Text>
														{isInverted && (
															<Tooltip title="앞 순번에 더 늦게 시작하는 시즌이 있습니다">
																<Chip
																	icon={<WarningAmber />}
																	label="순서 확인"
																	size="small"
																	variant="outlined"
																	className="invertedChip"
																/>
															</Tooltip>
														)}
													</div>
													<Text className="queueDate">
														{`${dayjs(aSeason.startDate).format('YYYY.MM.DD')} - ${dayjs(
															aSeason.endDate,
														).format('YYYY.MM.DD')}`}
													</Text>

													<Button
														size="small"
														variant="outlined"
														className="openButton"
														disabled={!isInSalePeriod}
														onClick={e => {
															e.stopPropagation()
															onOpenClick(aSeason)
														}}
													>
														{isInSalePeriod ? '판매 시작' : '기간 아님'}
													</Button>
												</div>

												<Tooltip title="대기열에서 제거">
													<IconButton
														size="small"
														className="removeButton"
														onClick={e => {
															e.stopPropagation()
															onRemoveClick(aSeason.id)
														}}
													>
														<Close fontSize="small" />
													</IconButton>
												</Tooltip>
											</div>
										)}
									</Draggable>
								)
							})}
							{droppableProvided.placeholder}
						</div>
					)}
				</Droppable>
			</DragDropContext>
		</View>
	)
}

const style = css({
	...boShared,
	width: '100%',

	'.emptyView': {
		padding: '40px 16px',
		textAlign: 'center',

		'.emptyText': {
			display: 'block',
			marginBottom: 6,
			fontFamily: 'inherit',
			fontSize: 14,
			fontWeight: 400,
			color: mono.dim,
			textAlign: 'center',
		},

		'.emptyHint': {
			display: 'block',
			fontFamily: 'inherit',
			fontSize: 13,
			fontWeight: 300,
			color: mono.faint,
			lineHeight: 1.6,
			textAlign: 'center',
		},
	},

	'.queueItem': {
		position: 'relative',
		display: 'flex',
		alignItems: 'flex-start',
		gap: 10,
		padding: '14px 8px 14px 4px',
		background: 'transparent',
		borderRadius: 0,
		borderBottom: `1px solid ${mono.line}`,
		borderLeft: '1px solid transparent',
		boxShadow: 'none',
		cursor: 'pointer',
		transition: boMotion,

		'&:hover': { background: mono.surface },

		'&.dragging': {
			background: mono.surface,
			borderLeftColor: mono.ink,
			borderBottomColor: mono.ink,
		},

		'&.selected': {
			background: mono.surface,
			borderLeftColor: mono.ink,
		},

		'.dragHandle': {
			display: 'flex',
			alignItems: 'center',
			height: 24,
			color: mono.faint,
			cursor: 'grab',

			'&:active': { cursor: 'grabbing' },
		},

		'.orderBadge': {
			display: 'flex',
			alignItems: 'center',
			flexShrink: 0,
			height: 24,
			fontSize: 11,
			letterSpacing: '.22em',
			color: mono.faint,
			fontVariantNumeric: 'tabular-nums',
		},

		'.queueContent': {
			flex: 1,
			minWidth: 0,
			paddingRight: 32,

			'.queueHeader': {
				display: 'flex',
				alignItems: 'center',
				gap: 8,
				minHeight: 24,

				'.queueTitle': {
					margin: 0,
					fontFamily: 'inherit',
					fontSize: 15,
					fontWeight: 400,
					color: mono.ink,
					width: 'auto',
				},

				'.invertedChip': {
					fontWeight: 500,
					'.MuiChip-icon': { color: mono.dim, fontSize: 14 },
				},
			},

			'.queueDate': {
				display: 'block',
				margin: '4px 0 10px',
				fontFamily: 'inherit',
				color: mono.dim,
				fontSize: 12.5,
				fontWeight: 300,
				fontVariantNumeric: 'tabular-nums',
				width: 'auto',
			},

			'.openButton': { fontWeight: 500 },
		},

		'.removeButton': {
			position: 'absolute',
			top: 10,
			right: 4,
		},
	},
})
